import { Fragment, useRef, useContext, useState } from "react";
import { Link } from "react-router-dom";
import {
  isAllowedToMakeCCPayment,
  isPolicyInactiveByPolicyStatus,
} from "js/utilities";
import { useTranslation } from "react-i18next";
import { getCalcfocusToken } from "service/userservice/getCalcfocusToken";
import { useSelector } from "react-redux";
import useTrackIllustraion from "logging/hooks/useTrackIllustraion";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import { AgentData } from "context/Agent/DataProvider";
import config from "config/config";
import * as fileSaver from "file-saver";
import { base64ToArrayBuffer } from "js/formatting";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import { downloadingForm } from "service/forms/downloadingForm";
import { getAgentFormsByPolicy } from "service/forms/getAgentFormsByPolicy";

const HelpfulLinks = ({ links, policy }) => {
  const { t } = useTranslation();
  const [calcfocusToken, setCalcfocusToken] = useState("");
  const navigateToCalcFocusform = useRef();
  const trackIllustraion = useTrackIllustraion();
  const { user } = useSelector((s) => s.user);
  const { agentPolicies } = useContext(AgentData);
  const [isSelectedLifeSysPolicy, setIsSelectedLifeSysPolicy] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const performSubmit = async () => {
    const selectedPolicy = agentPolicies.find(
      (p) => p.contractID === policy.contractID
    );

    if (selectedPolicy?.sourceSystemName?.toUpperCase() === "CALCFOCUS") {
      let res = await getCalcfocusToken(
        user?.userId,
        selectedPolicy?.oldPolicy
      );
      if (res?.token) {
        setCalcfocusToken(res?.token);
        setTimeout(() => {
          navigateToCalcFocusform.current.submit();

          trackIllustraion({
            ...user,
            provider: "Calcfocus",
          });
        }, 1);
      }
    } else {
      setIsSelectedLifeSysPolicy(true);
    }
  };

  const downloadForm = async () => {
    setFormLoading(true);

    await getAgentFormsByPolicy(policy.oldPolicy).then((res) => {
      const selected = res.form.filter(
        (form) => form.formTitle.split(" - ")[0] === "Request For Service Form"
      );

      downloadingForm(selected[0].id)
        .then((response) => {
          setFormLoading(false);
          let byteArray = base64ToArrayBuffer(response?.result);
          let blob = new Blob([byteArray], {
            type: "application/pdf",
          });
          fileSaver.saveAs(blob, `Request for Service Form.pdf`);
        })
        .catch((error) => {
          setFormLoading(false);
          console.error(error);
        });
    });
  };

  return (
    <>
      <div className="ui-card mb-4 md:mb-6">
        <div className="px-4 pt-4 pb-1 border-b border-theme-rule-light">
          <h2 className="text-[20px] font-body font-bold">
            {t("AgentHelpfulLinks-ThingsYouCanDo")}
          </h2>
        </div>
        <div className="p-4">
          <div className="grid gap-4 grid-flow-dense sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-1">
            {links.map((category) => (
              <div key={`things you can do - ${category.heading}`}>
                <div className="font-semibold mb-2">{category.heading}</div>
                <ul className="ml-2 text-sm">
                  {category.links.map((link) => {
                    if (
                      category.heading === "Manage Transactions" ||
                      category.heading === "Transactions"
                    ) {
                      if (link.name === "Make a Payment") {
                        return isAllowedToMakeCCPayment(
                          policy,
                          "onetimepayment"
                        ) && !isPolicyInactiveByPolicyStatus(policy?.status) ? (
                          <li
                            className="mb-2"
                            key={`things you can do - ${category.heading} - ${link.name}`}
                          >
                            <Link to={`${link.route}/${policy.contractID}`}>
                              {link.name}
                            </Link>
                          </li>
                        ) : (
                          <Fragment
                            key={`things you can do - ${category.heading} - ${link.name}`}
                          />
                        );
                      }

                      if (link.name === "View Billing Statements") {
                        return (
                          <li
                            className="mb-2"
                            key={`things you can do - ${category.heading} - ${link.name}`}
                          >
                            <Link
                              to={`${link.route}&id=${policy.oldPolicy}&category=PREMIUM NOTICE`}
                            >
                              {link.name}
                            </Link>
                          </li>
                        );
                      }
                    }

                    if (
                      category.heading === "Manage Policy" ||
                      category.heading === "Manage Contract"
                    ) {
                      if (link.name === "Quotes & Illustrations") {
                        return (
                          <div>
                            <form
                              method="POST"
                              action={config.CALCFOCUS_SERVICE_BASE_URL}
                              target="_blank"
                              ref={navigateToCalcFocusform}
                            >
                              <input
                                type="hidden"
                                name="jwtToken"
                                value={calcfocusToken}
                              />
                              <li
                                className="mb-2"
                                key={`things you can do - ${category.heading} - ${link.name}`}
                              >
                                <Link
                                  to="#"
                                  className="ui-link-icon"
                                  onClick={() => performSubmit()}
                                >
                                  {link.name}
                                </Link>
                              </li>
                            </form>
                          </div>
                        );
                      }

                      if (
                        link.name ===
                        "Update Contract Owner’s Contact Information"
                      ) {
                        return (
                          <EverlakeLoadingOverlay
                            isLoading={formLoading}
                            loadingText=""
                            spinnerSize="xs"
                          >
                            <li
                              className="mb-2"
                              key={`things you can do - ${category.heading} - ${link.name}`}
                            >
                              <button
                                className="ui-btn-hyperlink"
                                onClick={async () => {
                                  downloadForm();
                                }}
                              >
                                {link.name}
                              </button>
                            </li>
                          </EverlakeLoadingOverlay>
                        );
                      }

                      if (link.name === "View Forms") {
                        return (
                          <li
                            className="mb-2"
                            key={`things you can do - ${category.heading} - ${link.name}`}
                          >
                            <Link
                              to={`${link.route}?oldPolicy=${policy?.oldPolicy}`}
                            >
                              {link.name}
                            </Link>
                          </li>
                        );
                      }

                      if (link.name === "File a Claim") {
                        return (
                          <li
                            className="mb-2"
                            key={`things you can do - ${category.heading} - ${link.name}`}
                          >
                            <Link
                              to={`${link.route}?contractID=${policy?.contractID}`}
                            >
                              {link.name}
                            </Link>
                          </li>
                        );
                      }

                      if (link.name === "Fund Transfers") {
                        const prodType = policy?.product?.type?.toUpperCase();
                        if (["AVD", "LQ", "LZ"].includes(prodType)) {
                          return (
                            <li
                              className="mb-2"
                              key={`things you can do - ${category.heading} - ${link.name}`}
                            >
                              <Link
                                to={`${link.route}?oldPolicy=${policy?.oldPolicy}&keyword=fund transfer`}
                              >
                                {link.name}
                              </Link>
                            </li>
                          );
                        } else {
                          return (
                            <Fragment
                              key={`things you can do - ${category.heading} - ${link.name}`}
                            />
                          );
                        }
                      }

                      if (
                        link.name ===
                        "Fund Performance, Prospectuses & Fund Information"
                      ) {
                        const prodType = policy?.product?.type?.toUpperCase();
                        if (prodType === "AVD") {
                          return (
                            <li
                              className="mb-2"
                              key={`things you can do - ${category.heading} - ${link.name}`}
                            >
                              <a
                                href="https://www.everlakelife.com/policies-contracts/annuities/variable-annuities/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ui-link-icon"
                              >
                                {link.name}
                              </a>
                            </li>
                          );
                        } else if (prodType === "LQ" || prodType === "LZ") {
                          return (
                            <li
                              className="mb-2"
                              key={`things you can do - ${category.heading} - ${link.name}`}
                            >
                              <a
                                href="https://www.everlakelife.com/policies-contracts/life-insurance/variable-universal-life-insurance/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ui-link-icon"
                              >
                                {link.name}
                              </a>
                            </li>
                          );
                        } else {
                          return (
                            <Fragment
                              key={`things you can do - ${category.heading} - ${link.name}`}
                            />
                          );
                        }
                      }
                    }

                    if (category.heading === "Alerts & Documents")
                      return (
                        <li
                          className="mb-2"
                          key={`things you can do - ${category.heading} - ${link.name}`}
                        >
                          <Link to={`${link.route}&id=${policy.oldPolicy}`}>
                            {link.name}
                          </Link>
                        </li>
                      );

                    return (
                      <li
                        className="mb-2"
                        key={`things you can do - ${category.heading} - ${link.name}`}
                      >
                        <Link to={`${link.route}`}>{link.name}</Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
      <GeneralPurposeDialog
        state={isSelectedLifeSysPolicy}
        setState={setIsSelectedLifeSysPolicy}
        title={t("AgentPolicyLookUp-general-purpose-dialog-title")}
        desc={t("AgentPolicyLookUp-general-purpose-dialog-desc")}
        width="max-w-md"
        enableHrBlock={false}
        enablePrimaryButton={true}
        enableSecondaryButton={false}
        primaryButtonText={t("SecondaryButton-text")}
        primaryButtonAction={() => setIsSelectedLifeSysPolicy(false)}
      />
    </>
  );
};

export default HelpfulLinks;
